/* The moving tutorial, for the client that is actually shipping.
 *
 * The prototype has one and it is good: a spotlight that moves, a card that
 * follows it, and a narration that walks somebody through a product they have
 * never seen. A new tester meets it on their first run, and it is the only
 * copy anybody reads *before* they have a way to check it.
 *
 * The React client had nothing. Not a tour, not a first run, not a sentence —
 * a new user landed on an empty day and was left to work it out. So switching
 * clients would have quietly taken the introduction away from every person who
 * arrives after the switch, which is exactly the class of regression nobody
 * notices until a tester says "I did not know it could do that".
 *
 * Four rules, three of them learned from the prototype's own comments:
 *
 *   · **Every step must be true of THIS client.** The prototype's tour narrates
 *     dragging a block and a week screen, neither of which exists here, and it
 *     used to claim a Slack integration that was never built. A tour that
 *     overstates is worse than no tour, because it is read first and trusted
 *     most. Each step below points at something on screen right now.
 *
 *   · **Seen is recorded on START, not on a clean finish.** A tour that was
 *     abandoned — or that broke half way — must never reappear on the next
 *     load. The prototype learned this the hard way.
 *
 *   · **It never traps anybody.** Escape ends it, every step is skippable, the
 *     controls are real buttons in the tab order, and the whole overlay is
 *     announced. An unskippable tour is a hostage situation.
 *
 *   · **A step that cannot find its target does not break the app.** It
 *     centres itself and carries on. The tour is the least important thing on
 *     screen and must behave like it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { readLocal, writeLocal } from '../api';

const SEEN_KEY = 'amon_tour';

export interface TourStep {
  title: string;
  text: string;
  /** Where the step lives. The tour navigates there before measuring. */
  route?: string;
  /** What to put the spotlight on. Missing or not found: centred, no hole. */
  sel?: string;
  /** How long before it advances itself, in ms. */
  hold?: number;
}

/* Ten steps, in the order somebody meets the product rather than the order the
   navigation happens to be in. Each one names a thing that is on the screen it
   points at — and says what the thing is FOR, because "this is the month view"
   is not worth interrupting anybody for.
   
   Every selector points at a CONTAINER rather than at a row, and that is the
   whole lesson of the first version: `.db-table`, `.appt` and `.hobby` all
   exist — once you have a task, an appointment or a hobby. On the first run,
   which is the only run this tour is written for, the person has none of the
   three, so three of ten steps quietly narrated nothing at all. A tour has to
   point at the furniture, not at what somebody has put on it. */
export const STEPS: TourStep[] = [
  {
    title: 'A short tour',
    text: 'Amon builds a realistic day, defends it, and then does the part most planners skip — it makes sure the day actually happens. Two minutes, and you can leave at any point.',
    hold: 6500
  },
  {
    route: '/day',
    sel: '.timeline',
    title: 'Your day, in the order it happens',
    text: 'Morning, afternoon and evening are marked, every block shows when it starts and when it ends, and the line across it is now. Free gaps are labelled, because "when could I do this" is the question you came here with.',
    hold: 7000
  },
  {
    route: '/day',
    sel: '.block-actions',
    title: 'Nothing moves without you',
    text: 'Tick a block off, start a timer on it, move it, or take it off the day. Amon proposes; you decide. Anything you place by hand is pinned, and the planner works around it rather than over it.',
    hold: 7000
  },
  {
    route: '/day',
    sel: '.day-add',
    title: 'Two ways to give it work',
    text: 'Add something to the backlog and Amon finds the hours for it — that is the whole point. Or put it at a time you chose, for the things that are not yours to move.',
    hold: 6500
  },
  {
    route: '/month',
    sel: '.month',
    title: 'Is next month survivable?',
    text: 'The day says what you are doing now. The month answers the question you ask before saying yes to something in three weeks. Each cell shows what is fixed and how full the day is — never every block, which would be a wall of text.',
    hold: 7500
  },
  {
    route: '/tasks',
    sel: '.db',
    title: 'Everything you owe someone',
    text: 'Group it by status and it is a board, by type a project list, by when a schedule. Every column is a field the planner genuinely reads — there is no invented priority score here, because a column the solver ignores would quietly teach you it is not listening.',
    hold: 8000
  },
  {
    /* The appointment step moved to the day, where appointments now live: a
       show, a dentist and a flight are things you look at on a date, and the
       backlog was the wrong place to read one. */
    route: '/day',
    sel: '.day-add',
    title: 'And the things you do not choose the time of',
    text: 'A show, a dentist, a flight — once, every week, or every year, all from here. For an appointment Amon holds the travel either side as real time on your day, puts the getting-ready in your tasks so it is planned in the days before, and warns you early enough to be useful.',
    hold: 8000
  },
  {
    route: '/hobbies',
    sel: '#must-title',
    title: 'The week is not only work',
    text: 'A hobby is a name and an amount of time a week. Amon marks it as not-work — which is what lets it reach an evening or a weekend at all — and holds that time back before offering your week to anything else.',
    hold: 7500
  },
  {
    route: '/hobbies',
    sel: '#free-title',
    title: 'Time that is yours',
    text: 'The free hours in the week ahead, outside your working hours — the evenings and weekends nothing is planned in. Tap one to put a hobby or a friend in it. Nothing to earn and nothing to spend: it is simply your time, shown.',
    hold: 8000
  },
  {
    title: "That is the tour",
    text: 'Add what you owe someone, then press Replan and Amon will place it around what is already fixed. Press ? on the day for the keyboard, and G to see this again.',
    hold: 9000
  }
];

export const tourSeen = (): boolean => readLocal(SEEN_KEY) === '1';
export const markTourSeen = (): void => writeLocal(SEEN_KEY, '1');

/* Asking for it again, from anywhere.
 *
 * An event rather than a context, deliberately: a provider, a hook and a
 * consumer to carry one boolean to one button is more machinery than the thing
 * it carries, and every screen that wanted to offer a replay would have to be
 * wrapped in it. The tour is owned by App, which is the only place that can
 * legitimately draw over the whole app. */
export const TOUR_EVENT = 'amon:tour';
export const startTour = (): void => { window.dispatchEvent(new Event(TOUR_EVENT)); };

const PAD = 8;

export default function Tour({ steps = STEPS, onClose }: {
  steps?: TourStep[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [spot, setSpot] = useState<DOMRect | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const step = steps[i];
  const last = i === steps.length - 1;

  /* Recorded the moment it starts. A tour somebody abandoned, or one that
     broke, must not come back on the next load. */
  useEffect(() => { markTourSeen(); }, []);

  const end = useCallback(() => { markTourSeen(); onClose(); }, [onClose]);

  /* Navigate, then keep looking until it is there.
   *
   * The first version measured twice — two animation frames after the route
   * change — and gave up. Every single step centred itself, because the thing
   * being pointed at had not arrived yet: a route change renders a *skeleton*
   * while its query is in flight, and the timeline, the table and the month
   * grid all appear a few hundred milliseconds later. Measuring once and
   * accepting the answer means measuring the loading state.
   *
   * So this tracks instead of measuring: one loop, reading the rect every
   * frame, writing only when it actually moved. That covers the late arrival,
   * the smooth scroll that follows it, a window resize, an image loading above
   * it, and the row that grows when a query settles — all of which move the
   * thing under the spotlight, and none of which fire a scroll event.
   *
   * Cheap: a rect read is layout-only, and `setSpot` is skipped unless the
   * rounded position changed, so a settled step re-renders nothing. */
  useEffect(() => {
    if (step.route) navigate(step.route);

    let raf = 0;
    let key = '';
    let scrolled = false;

    /* One measurement that does not wait for a frame.
     *
     * `requestAnimationFrame` does not fire in a hidden tab — zero frames, for
     * as long as it stays hidden — so a tour that only ever measures inside
     * the loop opens with no spotlight at all for somebody who started it and
     * switched away. The loop below is for tracking; this is for arriving. */
    const measure = () => {
      const el = step.sel ? document.querySelector(step.sel) : null;
      if (el) {
        const r = el.getBoundingClientRect();
        const next = `${Math.round(r.top)},${Math.round(r.left)},${Math.round(r.width)},${Math.round(r.height)}`;
        if (next !== key) { key = next; setSpot(r); }
        /* Once, when it first appears — not every frame, which would fight
           the person's own scrolling for as long as the step is up. */
        if (!scrolled) { scrolled = true; el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      } else if (key !== 'none') {
        key = 'none';
        setSpot(null);
      }
    };

    const tick = () => { measure(); raf = requestAnimationFrame(tick); };

    measure();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [i, step.route, step.sel, navigate]);

  /* Auto-advance, unless somebody paused it or asked for less motion. */
  useEffect(() => {
    if (!playing) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const t = setTimeout(() => {
      if (last) end(); else setI(n => n + 1);
    }, step.hold ?? 7000);
    return () => clearTimeout(t);
  }, [i, playing, step.hold, last, end]);

  /* The card takes focus on every step, so a screen reader reads the new step
     rather than leaving somebody on a button that has just changed meaning. */
  useEffect(() => { cardRef.current?.focus(); }, [i]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.stopPropagation(); end(); }
    if (e.key === 'ArrowRight') { e.preventDefault(); last ? end() : setI(n => n + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); setI(n => Math.max(0, n - 1)); }
  }

  /* Where the card goes: under the spotlight when there is room, above it when
     there is not, centred when there is nothing to point at. */
  const card = (() => {
    const w = 340, h = cardRef.current?.offsetHeight || 210;
    if (!spot) return {
      top: Math.max(16, window.innerHeight / 2 - h / 2),
      left: Math.max(12, window.innerWidth / 2 - w / 2)
    };
    let top = spot.bottom + 16;
    if (top + h > window.innerHeight - 12) top = Math.max(12, spot.top - h - 16);
    let left = spot.left;
    if (left + w > window.innerWidth - 12) left = window.innerWidth - w - 12;
    return { top: Math.min(Math.max(12, top), Math.max(12, window.innerHeight - h - 12)),
      left: Math.max(12, left) };
  })();

  return (
    <div className="tour is-on" onKeyDown={onKeyDown}>
      {/* The hole. A box-shadow the size of the viewport is what dims
          everything except the thing being talked about, without covering it
          with anything that could swallow a click. */}
      {/* Clamped to what is actually on screen.
       *
       * The day's timeline is 2,183 pixels tall. Spotlighting it honestly
       * means a hole bigger than the window, which dims nothing and reads as
       * a broken overlay rather than as emphasis. A spotlight is a claim
       * about where to look, and it can only point at the part you can see. */}
      <div className="tour-spot" aria-hidden="true" style={spot ? (() => {
        /* A viewport of zero is not a viewport. It happens in an embedded or
           offscreen frame, and clamping to it collapses the spotlight to a
           dot — so when the window cannot say how big it is, the rect is
           trusted as measured. */
        const vh = window.innerHeight || 0, vw = window.innerWidth || 0;
        const top = Math.max(8, spot.top - PAD);
        const left = Math.max(8, spot.left - PAD);
        const bottom = vh ? Math.min(vh - 8, spot.bottom + PAD) : spot.bottom + PAD;
        const right = vw ? Math.min(vw - 8, spot.right + PAD) : spot.right + PAD;
        return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
      })() : { top: '50%', left: '50%', width: 0, height: 0 }} />

      <div ref={cardRef} className="tour-card" style={card}
           role="dialog" aria-modal="false" aria-labelledby="tour-title"
           tabIndex={-1}>
        <p className="tour-count" aria-live="polite">Step {i + 1} of {steps.length}</p>
        <h2 id="tour-title">{step.title}</h2>
        <p>{step.text}</p>

        <div className="tour-controls">
          <button className="btn btn-ghost btn-sm" onClick={end}>Skip the tour</button>
          <span className="tour-spacer" />
          <button className="btn btn-ghost btn-sm" onClick={() => setPlaying(p => !p)}
                  aria-pressed={!playing}>
            {playing ? 'Pause' : 'Play'}
          </button>
          <button className="btn btn-ghost btn-sm" disabled={i === 0}
                  onClick={() => setI(n => Math.max(0, n - 1))}>Back</button>
          <button className="btn btn-primary btn-sm"
                  onClick={() => last ? end() : setI(n => n + 1)}>
            {last ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
